import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import type { Subscription } from 'rxjs';

import type { SearchService } from '@/services/search-service';
import { appReadyAtom } from '@/state';

// Runs refetch when the given instance's indexUpdateNotifier emits, but only while that
// index is ready and the app is ready. While the index is not ready
// (initializing/rebuilding/restore-failed) no refetch fires, so no search message is
// sent against an unready index and callers keep their loading state. When the index
// transitions to ready, the indexUpdateNotifier replay drives a refetch.
export const useSearchIndexUpdateListener = (options: Options) => {
  const { service, refetch, enabled = true } = options;
  const appReady = useAtomValue(appReadyAtom);

  useEffect(() => {
    if (!appReady || !enabled) {
      return;
    }

    let updateSubscription: Subscription | undefined;

    const statusSubscription = service.indexStatus.subscribe((status) => {
      updateSubscription?.unsubscribe();
      updateSubscription = status === 'ready' ? service.indexUpdateNotifier.subscribe(refetch) : undefined;
    });

    return () => {
      statusSubscription.unsubscribe();
      updateSubscription?.unsubscribe();
    };
  }, [service, refetch, appReady, enabled]);
};

interface Options {
  service: SearchService;
  refetch: () => void;
  enabled?: boolean;
}
